// Servicio de Analytics para Google Sheets
// Este servicio registra visitas de forma transparente sin mostrar nada en la interfaz

import ANALYTICS_CONFIG from '../config/analyticsConfig';

class AnalyticsService {
    constructor() {
        this.isInitialized = false;
        this.visitRegistered = false;
        this.sessionId = this.generateSessionId();
    }

    // Generar ID único de sesión
    generateSessionId() {
        return Date.now().toString(36) + Math.random().toString(36).substr(2);
    }

    // Inicializar el servicio
    init() {
        if (this.isInitialized) return;
        
        this.isInitialized = true;
        
        // Registrar visita cuando se carga la página
        this.registerPageVisit();
        
        // También registrar en cambios de ruta (SPA)
        if (ANALYTICS_CONFIG.TRACKING.ROUTE_CHANGES) {
            this.setupRouteChangeTracking();
        }
    
    }

    // Registrar visita de página
    async registerPageVisit() {
        // Evitar registrar múltiples veces en la misma sesión
        if (ANALYTICS_CONFIG.TRACKING.SESSION_BASED && this.visitRegistered) {
            return;
        }
        
        try {
            const ahora = new Date();
            
            // Formatear fecha y hora para Colombia
            const fecha = ahora.toLocaleDateString(ANALYTICS_CONFIG.TIMEZONE, {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            });
            
            const hora = ahora.toLocaleTimeString(ANALYTICS_CONFIG.TIMEZONE, {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            });
            
            // Crear formulario con los datos
            const formData = new FormData();
            formData.append(ANALYTICS_CONFIG.FIELDS.FECHA, fecha);
            formData.append(ANALYTICS_CONFIG.FIELDS.HORA, hora);
            formData.append(ANALYTICS_CONFIG.FIELDS.URL, window.location.href);
            
            if (ANALYTICS_CONFIG.TRACKING.USER_AGENT) {
                formData.append(ANALYTICS_CONFIG.FIELDS.USER, this.getUserAgent());
            }
            
            // Enviar datos al Google Form de forma asíncrona
            await this.sendToGoogleForm(formData);
            
            // Marcar como registrado para esta sesión
            this.visitRegistered = true;
        
            
        } catch (error) {
        }
    }

    // Enviar datos a Google Form
    async sendToGoogleForm(formData) {
        try {
            // Usar fetch con no-cors para evitar problemas de CORS
            await fetch(ANALYTICS_CONFIG.FORM_URL, {
                method: 'POST',
                mode: 'no-cors',
                body: formData
            });
        } catch (error) {
            // Fallback: usar imagen pixel para tracking
            if (ANALYTICS_CONFIG.TRACKING.FALLBACK_ENABLED) {
                this.fallbackTracking();
            }
        }
    }

    // Método fallback usando imagen pixel
    fallbackTracking() {
        try {
            const img = new Image();
            const params = new URLSearchParams({
                [ANALYTICS_CONFIG.FIELDS.FECHA]: new Date().toLocaleDateString(ANALYTICS_CONFIG.TIMEZONE),
                [ANALYTICS_CONFIG.FIELDS.HORA]: new Date().toLocaleTimeString(ANALYTICS_CONFIG.TIMEZONE),
                [ANALYTICS_CONFIG.FIELDS.URL]: window.location.href
            });
            
            if (ANALYTICS_CONFIG.TRACKING.USER_AGENT) {
                params.append(ANALYTICS_CONFIG.FIELDS.USER, this.getUserAgent());
            }
            
            img.src = `${ANALYTICS_CONFIG.FORM_URL}?${params}`;
        } catch (error) {
            
        }
    }

    // Obtener user agent limpio
    getUserAgent() {
        try {
            const ua = navigator.userAgent;
            // Limpiar información sensible y limitar longitud
            return ua.replace(/[^\w\s\-\.]/g, '').substring(0, 100);
        } catch {
            return 'Unknown';
        }
    }

    // Configurar tracking de cambios de ruta (para SPAs)
    setupRouteChangeTracking() {
        // Detectar cambios de ruta usando History API
        let currentUrl = window.location.href;
        
        const checkUrlChange = () => {
            if (window.location.href !== currentUrl) {
                currentUrl = window.location.href;
                // Registrar nueva visita en nueva ruta
                this.registerPageVisit();
            }
        };

        // Verificar cambios cada 100ms
        setInterval(checkUrlChange, 100);
        
        // También escuchar eventos de popstate
        window.addEventListener('popstate', () => {
            setTimeout(() => this.registerPageVisit(), 100);
        });
    }

    // Método para registrar visita manual (útil para testing)
    forceRegister() {
        this.visitRegistered = false;
        this.registerPageVisit();
    }

    // Sistema de logging configurable
    log(level, message, data = {}) {
        if (!ANALYTICS_CONFIG.LOGGING.ENABLED) return;
        
        const timestamp = new Date().toISOString();
        const logData = {
            timestamp,
            level,
            message,
            sessionId: this.sessionId,
            ...data
        };
        
        switch (level) {
            case 'info':
                break;
            case 'warn':
                break;
            case 'error':
                break;
        }
    }

    // Obtener estadísticas de la sesión
    getSessionStats() {
        return {
            isInitialized: this.isInitialized,
            visitRegistered: this.visitRegistered,
            sessionId: this.sessionId,
            currentUrl: window.location.href
        };
    }
}

// Crear instancia única
const analyticsService = new AnalyticsService();

export default analyticsService;
